// src/components/Modal.tsx
//
// Shared modal shell. Solves the recurring "modal closes when I drag-select
// text" bug by tracking the mousedown ORIGIN — the backdrop closes ONLY when
// both mousedown AND mouseup happen on the backdrop itself. The naive
// `onClick={onClose}` + `stopPropagation()` pattern bubbles text-selection
// mouseup events to the backdrop and dismisses the modal mid-edit.
//
// Always use this component for new modals. Do NOT introduce
// `onClick={onClose}` directly on a backdrop div.

import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

interface ModalProps {
  title?: string;
  onClose: () => void;
  children: React.ReactNode;
  /** Disable backdrop-click-to-close (useful for required modals like tenant picker). */
  closeOnBackdrop?: boolean;
  /** Disable Escape-to-close. */
  closeOnEsc?: boolean;
  /** Override the inner panel width. Defaults to `max-w-md`. */
  panelClassName?: string;
  /** Show the title bar with X button. Defaults to true when `title` is provided. */
  showHeader?: boolean;
}

export default function Modal({
  title,
  onClose,
  children,
  closeOnBackdrop = true,
  closeOnEsc = true,
  panelClassName = 'max-w-md',
  showHeader,
}: ModalProps) {
  // Track whether the pointer DRAG started on the backdrop. Closing on backdrop
  // is gated on this — a drag that starts inside the panel and releases outside
  // (e.g. selecting text past the panel edge) MUST NOT close the modal.
  const downOnBackdrop = useRef(false);

  useEffect(() => {
    if (!closeOnEsc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [closeOnEsc, onClose]);

  const renderHeader = showHeader ?? Boolean(title);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onMouseDown={(e) => {
        downOnBackdrop.current = e.target === e.currentTarget;
      }}
      onMouseUp={(e) => {
        const wasBackdropDrag = downOnBackdrop.current && e.target === e.currentTarget;
        downOnBackdrop.current = false;
        if (wasBackdropDrag && closeOnBackdrop) onClose();
      }}
    >
      <div
        className={`bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full ${panelClassName} mx-4 p-6 max-h-[90vh] overflow-auto`}
        // Belt-and-suspenders: even if a child somehow propagates a mouse event,
        // these listeners reset the backdrop tracking so the backdrop never sees
        // a drag that began inside the panel.
        onMouseDown={(e) => { downOnBackdrop.current = false; e.stopPropagation(); }}
        onMouseUp={(e) => e.stopPropagation()}
      >
        {renderHeader && (
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-base font-semibold text-white">{title}</h3>
            <button
              onClick={onClose}
              className="p-1 rounded text-slate-500 hover:text-white hover:bg-slate-800 transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
